var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useMemo, useState } from 'react';
import { useInterval } from '../../common/hooks';
var useCarousel = function (_a) {
    var children = _a.children, _b = _a.slideToShow, slideToShow = _b === void 0 ? 1 : _b, _c = _a.transition, transition = _c === void 0 ? 1000 : _c, _d = _a.autoplaySpeed, autoplaySpeed = _d === void 0 ? 3000 : _d, _e = _a.isAutoplay, isAutoplay = _e === void 0 ? false : _e;
    var _f = useState([]), itemList = _f[0], setItemList = _f[1];
    var _g = useState(0), coordinateX = _g[0], setCoordinateX = _g[1];
    var _h = useState(isAutoplay), autoPlayStatus = _h[0], setAutoPlayStatus = _h[1];
    var _j = useState(0), transitionTime = _j[0], setTransitionTime = _j[1];
    var _k = useState(false), disable = _k[0], setDisable = _k[1];
    var _l = useState(0), forwardItemLength = _l[0], setForwardItemLength = _l[1];
    var _m = useState(0), showIndex = _m[0], setShowIndex = _m[1];
    var childrenLength = React.Children.toArray(children).length;
    var itemLength = useMemo(function () { return itemList.length; }, [itemList]);
    var startTransition = function (index) {
        setDisable(true);
        setTransitionTime(transition);
        setShowIndex(index);
    };
    var endTransition = function () {
        setTimeout(function () {
            setDisable(false);
            setShowIndex(forwardItemLength);
        }, transition);
    };
    var duringTransition = function (direction) {
        setTimeout(function () {
            setTransitionTime(0);
            if (direction === 'next') {
                setItemList(function (prev) {
                    var result = setNextItem({ prevList: prev, showIndex: showIndex, slideToShow: slideToShow, childrenLength: childrenLength }).list;
                    return result;
                });
            }
            else {
                setItemList(function (prev) {
                    var result = setPreviousItem({ prevList: prev, showIndex: showIndex, slideToShow: slideToShow, childrenLength: childrenLength }).list;
                    return result;
                });
            }
        }, transition);
    };
    var showPrev = function () {
        if (disable)
            return;
        startTransition(showIndex - slideToShow);
        duringTransition('prev');
        endTransition();
    };
    var showNext = function () {
        if (disable)
            return;
        startTransition(showIndex + slideToShow);
        duringTransition('next');
        endTransition();
    };
    var onTouchStart = function (e) {
        setCoordinateX(e.touches[0].clientX);
    };
    var onTouchEnd = function (e) {
        if (coordinateX - e.changedTouches[0].clientX > 100)
            showNext();
        if (e.changedTouches[0].clientX - coordinateX > 100)
            showPrev();
        setCoordinateX(0);
    };
    var onMouseDown = function (e) {
        setCoordinateX(e.clientX);
    };
    var onMouseUp = function (e) {
        if (coordinateX - e.clientX > 100)
            showNext();
        if (e.clientX - coordinateX > 100)
            showPrev();
        setCoordinateX(0);
    };
    var playCarousel = function () {
        setAutoPlayStatus(true);
    };
    var stopPlayCarousel = function () {
        setAutoPlayStatus(false);
    };
    useInterval(function () {
        if (autoPlayStatus)
            showNext();
        else
            return;
    }, autoplaySpeed, [showIndex, autoPlayStatus, disable]);
    useEffect(function () {
        var _a = init({ children: children, slideToShow: slideToShow }), list = _a.list, index = _a.index, length = _a.length;
        setShowIndex(index);
        setForwardItemLength(length);
        setItemList(list);
    }, []);
    useEffect(function () {
        setTransitionTime(0);
    }, []);
    return {
        itemList: itemList,
        showIndex: showIndex,
        itemLength: itemLength,
        transitionTime: transitionTime,
        listeners: {
            onMouseDown: onMouseDown,
            onMouseUp: onMouseUp,
            onTouchEnd: onTouchEnd,
            onTouchStart: onTouchStart,
            showNext: showNext,
            showPrev: showPrev,
            playCarousel: playCarousel,
            stopPlayCarousel: stopPlayCarousel,
        },
    };
};
export { useCarousel };
var init = function (_a) {
    var children = _a.children, slideToShow = _a.slideToShow;
    var list = React.Children.toArray(children);
    var showItems = list.slice(0, slideToShow);
    var restItems = list.slice(slideToShow);
    var backwardItems = restItems.slice(0, Math.floor(restItems.length / 2));
    var forwardItems = restItems.slice(Math.floor(restItems.length / 2));
    var result = __spreadArray(__spreadArray(__spreadArray([], forwardItems, true), showItems, true), backwardItems, true);
    var showIndex = 0;
    var forwardItemLength = 0;
    if (Math.floor(list.length / slideToShow) <= slideToShow) {
        var f = result.slice(0, slideToShow);
        var b = result.slice(-slideToShow);
        result.push.apply(result, f);
        result.unshift.apply(result, b);
        showIndex = forwardItems.length + slideToShow;
        forwardItemLength = forwardItems.length + slideToShow;
    }
    else {
        showIndex = forwardItems.length;
        forwardItemLength = forwardItems.length;
    }
    return { list: result, index: showIndex, length: forwardItemLength };
};
var setNextItem = function (_a) {
    var prevList = _a.prevList, childrenLength = _a.childrenLength, slideToShow = _a.slideToShow, showIndex = _a.showIndex;
    var list = Math.floor(childrenLength / slideToShow) <= slideToShow
        ? __spreadArray([], prevList, true).splice(slideToShow, childrenLength)
        : __spreadArray([], prevList, true);
    var result = [];
    var index = showIndex - childrenLength / slideToShow + 1;
    if (Math.floor(childrenLength / slideToShow) <= slideToShow) {
        for (var i = 0; i < slideToShow; i++) {
            result.push(list.shift());
        }
        list.push.apply(list, result);
        var forward = list.slice(0, slideToShow);
        var backward = list.slice(-slideToShow);
        list.push.apply(list, forward);
        list.unshift.apply(list, backward);
    }
    else {
        for (var i = 0; i < slideToShow; i++) {
            result.push(list.shift());
        }
        list.push.apply(list, result);
    }
    return { list: list, index: index };
};
var setPreviousItem = function (_a) {
    var prevList = _a.prevList, childrenLength = _a.childrenLength, slideToShow = _a.slideToShow, showIndex = _a.showIndex;
    var list = Math.floor(childrenLength / slideToShow) <= slideToShow
        ? __spreadArray([], prevList, true).splice(slideToShow, childrenLength)
        : __spreadArray([], prevList, true);
    var result = [];
    var index = showIndex + childrenLength / slideToShow - 1;
    if (Math.floor(childrenLength / slideToShow) <= slideToShow) {
        for (var i = 0; i < slideToShow; i++) {
            result.unshift(list.pop());
        }
        list.unshift.apply(list, result);
        var forward = list.slice(0, slideToShow);
        var backward = list.slice(-slideToShow);
        list.push.apply(list, forward);
        list.unshift.apply(list, backward);
    }
    else {
        for (var i = 0; i < slideToShow; i++) {
            result.unshift(list.pop());
        }
        list.unshift.apply(list, result);
    }
    return { list: list, index: index };
};
